import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LogoIncluirDto, LogoIncluirSchema } from "./Logo.Dto";
import {
  EmpresaBuscarLogo,
  EmpresaRemoverLogo,
  EmpresaUploadLogo,
} from "src/services/Entidade.Service";
import {
  MapearErrosServidor,
  MapearErrosServidorCampo,
} from "src/hook/Functions";
import { showToast } from "src/componentes";
import { showMessageBox } from "src/componentes/MessageBox/Messagebox";

const LogoCode = () => {
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [hasLogo, setHasLogo] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [previewModal, setPreviewModal] = useState<string | null>(null);
  const [show, setShow] = useState(false);

  const logoUrlRef = useRef<string | null>(null);
  logoUrlRef.current = logoUrl;

  const previewModalRef = useRef<string | null>(null);
  previewModalRef.current = previewModal;

  const {
    register: registerIncluir,
    handleSubmit: handleSubmitIncluir,
    formState: { errors: errorsIncluir },
    setError: setErrorIncluir,
    reset: resetIncluir,
  } = useForm<LogoIncluirDto>({
    resolver: yupResolver(LogoIncluirSchema),
    defaultValues: undefined,
  });

  const handleClose = () => {
    setShow(false);
    resetIncluir();
    if (previewModalRef.current) {
      URL.revokeObjectURL(previewModalRef.current);
      setPreviewModal(null);
    }
  };

  const handleShow = () => setShow(true);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (previewModalRef.current) {
        URL.revokeObjectURL(previewModalRef.current);
      }
      const newPreview = URL.createObjectURL(file);
      setPreviewModal(newPreview);
    } else {
      if (previewModalRef.current) {
        URL.revokeObjectURL(previewModalRef.current);
      }
      setPreviewModal(null);
    }
  };

  const carregarLogo = async () => {
    setLoading(true);
    try {
      const response = await EmpresaBuscarLogo();
      if (response?.status === 200 && response?.data && response?.data?.size > 0) {
        if (logoUrlRef.current) {
          URL.revokeObjectURL(logoUrlRef.current);
        }
        const url = URL.createObjectURL(response.data);
        setLogoUrl(url);
        setHasLogo(true);
      } else {
        setHasLogo(false);
        setLogoUrl(null);
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setHasLogo(false);
        if (logoUrlRef.current) {
          URL.revokeObjectURL(logoUrlRef.current);
        }
        setLogoUrl(null);
      } else {
        console.error("Erro ao buscar logo:", error);
        showToast({
          message: "Ocorreu erro ao buscar logo da empresa",
          title: "Erro",
          variant: "Danger",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarLogo();
    return () => {
      if (logoUrlRef.current) {
        URL.revokeObjectURL(logoUrlRef.current);
      }
      if (previewModalRef.current) {
        URL.revokeObjectURL(previewModalRef.current);
      }
    };
  }, []);

  const onSubmitIncluir = async (data: LogoIncluirDto) => {
    if (!data.arquivo || data.arquivo.length === 0) return;
    const file = data.arquivo[0] as unknown as File;
    setSalvando(true);

    try {
      const response = await EmpresaUploadLogo(file);
      if (response.data?.success) {
        showToast({
          message: "Logo salva com sucesso!",
          title: "Sucesso",
          variant: "Success",
        });
        handleClose();
        await carregarLogo();
      } else {
        MapearErrosServidorCampo(
          response.data?.notifications,
          setErrorIncluir
        );
        MapearErrosServidor(response.data?.notifications, showToast);
      }
    } catch (error) {
      console.error("Erro ao salvar logo:", error);
      showToast({
        message: "Ocorreu erro ao enviar a logo da empresa",
        title: "Erro",
        variant: "Danger",
      });
    } finally {
      setSalvando(false);
    }
  };

  const handleRemover = () => {
    showMessageBox(
      "Deseja realmente remover a logo da empresa?",
      "Atenção!",
      "YesNo",
      "Warning",
      {
        onYes: async () => {
          setLoading(true);
          try {
            const response = await EmpresaRemoverLogo();
            if (response.data?.success) {
              showToast({
                message: "Logo removida com sucesso!",
                title: "Sucesso",
                variant: "Success",
              });
              if (logoUrlRef.current) {
                URL.revokeObjectURL(logoUrlRef.current);
              }
              setLogoUrl(null);
              setHasLogo(false);
            } else {
              MapearErrosServidor(response.data?.notifications, showToast);
            }
          } catch (error) {
            console.error("Erro ao remover logo:", error);
            showToast({
              message: "Ocorreu erro ao remover logo da empresa",
              title: "Erro",
              variant: "Danger",
            });
          } finally {
            setLoading(false);
          }
        },
      }
    );
  };

  return {
    loading,
    salvando,
    hasLogo,
    logoUrl,
    previewModal,
    show,
    handleShow,
    handleClose,
    handleFileChange,
    handleSubmitIncluir,
    onSubmitIncluir,
    registerIncluir,
    errorsIncluir,
    handleRemover,
    carregarLogo,
  };
};

export default LogoCode;
